import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useCreditsStore } from '../../store/creditsStore';
import { supabase } from '../../lib/supabase';
import { JobCard } from '../../components/JobCard';
import {
  Heart,
  X,
  RotateCcw,
  Coins,
  Briefcase,
  Sparkles,
  Zap,
  Check,
  FileText,
  ArrowRight,
  Upload,
  FileUp,
  Search,
  SlidersHorizontal,
  MapPin,
  DollarSign,
  RefreshCw,
  Trash2,
  Eye,
  EyeOff,
} from 'lucide-react';
import type { Database } from '../../lib/supabase';
import { Navigation } from '../../components/Navigation';
import { resumeService } from '../../lib/resumeGenerationService';
import { resumeTemplateService } from '../../lib/resumeTemplateService';
import { useNavigate } from 'react-router-dom';
import { seed100Jobs } from '../../lib/seed100Jobs';
import { getOrCreateSystemEmployer } from '../../lib/systemEmployerService';

type Job = Database['public']['Tables']['jobs']['Row'];

// Hoisted helper: classify job cost tier
function determineApplicationType(job: Job): 'entry_level' | 'mid_level' | 'high_paying' {
  if (job.salary_min && job.salary_min >= 80000) return 'high_paying';
  if (job.experience_level === 'senior' || job.experience_level === 'executive') return 'high_paying';
  if (job.experience_level === 'mid') return 'mid_level';
  return 'entry_level';
}

// Hoisted helper: map tier → credit cost
function getApplicationCost(applicationType: 'entry_level' | 'mid_level' | 'high_paying'): number {
  const costs = { entry_level: 2, mid_level: 3, high_paying: 5 };
  return costs[applicationType];
}

type ToastType = 'info' | 'success' | 'error' | 'warning';

interface ToastState {
  type: ToastType;
  message: string;
}

export function SwipePage() {
  const { profile } = useAuthStore();
  const { balance, fetchCredits, awardCredits } = useCreditsStore();
  const navigate = useNavigate();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swipeHistory, setSwipeHistory] = useState<Array<{ jobId: string; direction: 'left' | 'right' }>>([]);
  const [autoSeeding, setAutoSeeding] = useState(false);
  const [seedingComplete, setSeedingComplete] = useState(false);

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [userResumes, setUserResumes] = useState<any[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null);
  const [defaultResumeId, setDefaultResumeId] = useState<string | null>(null);
  const [applyingJob, setApplyingJob] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);

  const [showFreeApplyModal, setShowFreeApplyModal] = useState(false);
  const [freeApplyMode, setFreeApplyMode] = useState<'upload' | 'paste'>('upload');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState('');
  const [coverLetter, setCoverLetter] = useState('');
  const [submittingFreeApp, setSubmittingFreeApp] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    experienceLevel: [] as string[],
    jobType: [] as string[],
    remote: null as boolean | null,
    minSalary: null as number | null,
    maxSalary: null as number | null,
    location: '',
  });

  // View mode for job history
  type ViewMode = 'new' | 'skipped' | 'saved';
  const [viewMode, setViewMode] = useState<ViewMode>('new');
  const [swipeCounts, setSwipeCounts] = useState({ skipped: 0, saved: 0, new: 0 });
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // 🔔 Toast state (non-repeating messages)
  const [toast, setToast] = useState<ToastState | null>(null);
  const [toastIndex, setToastIndex] = useState<{ right: number; left: number; lowCredits: number }>({
    right: 0,
    left: 0,
    lowCredits: 0,
  });

  const rightSwipeMessages = [
    'Saved! We’ll keep this one on your radar.',
    'Locked in. This job’s now in your shortlist.',
    'Nice swipe — added to your opportunities.',
    'Good pick. We saved this for you.',
    'This job looks promising. Saved!',
    'Got it! We’ll track this one for you.',
    'Marked as interested. Great choice!',
    'Saved — we’ll notify you if anything changes.',
    'You’re on a roll. Added to your list.',
    'Saved! Let’s keep the momentum going.',
  ];

  const leftSwipeMessages = [
    'Skipped — on to the next opportunity.',
    'No worries, we’ll find something better.',
    'Passed. Keep swiping!',
    'Not your vibe? Onward.',
    'Skipped — we’ll tune your matches.',
    'On to the next one.',
    'Passed. Your next match might be the one.',
    'Skip saved. Searching for better fits.',
    'Got it — we’ll refine your job feed.',
    'Skipped. Let’s find something you’ll love.',
  ];

  const lowCreditMessages = [
    'We saved this job, but you don’t have enough credits for AI auto-apply yet.',
    'Saved — add more credits to unlock instant AI applications.',
    'Job saved. Top up credits to let AI apply for you automatically.',
    'Saved! AI auto-apply paused — credits too low.',
  ];

  const getNextToastMessage = (kind: 'right' | 'left' | 'lowCredits') => {
    const lists = {
      right: rightSwipeMessages,
      left: leftSwipeMessages,
      lowCredits: lowCreditMessages,
    };
    const list = lists[kind];
    const current = toastIndex[kind] ?? 0;
    const message = list[current];

    setToastIndex(prev => ({
      ...prev,
      [kind]: (current + 1) % list.length,
    }));

    return message;
  };

  const showToast = (type: ToastType, message: string) => {
    setToast({ type, message });
    setTimeout(() => {
      setToast(null);
    }, 3500);
  };

  // 🃏 Animation state for programmatic swipe (X / Heart buttons)
  const [cardSwipeAnimation, setCardSwipeAnimation] = useState<'left' | 'right' | null>(null);

  useEffect(() => {
    if (profile?.id) {
      console.log('[SwipePage] Loading data for user:', profile.id);
      fetchCredits(profile.id).catch(err => {
        console.error('[SwipePage] Error fetching credits:', err);
      });
      checkAndAutoSeedJobs().then(() => {
        loadJobs();
      });
      loadUserResumes();
    } else {
      console.warn('[SwipePage] Profile not available yet');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (profile?.id) {
      loadJobs();
      loadSwipeCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchQuery, viewMode]);

  const checkAndAutoSeedJobs = async () => {
    const hasSeeded = localStorage.getItem('resuswipe_jobs_seeded');
    if (hasSeeded === 'true' || seedingComplete) {
      return;
    }

    try {
      const { count, error } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      if (error) {
        console.error('[AutoSeed] Error checking job count:', error);
        return;
      }

      if (count === 0) {
        console.log('[AutoSeed] No jobs found. Starting automatic seeding...');
        setAutoSeeding(true);

        const systemEmployerId = await getOrCreateSystemEmployer();
        if (!systemEmployerId) {
          console.error('[AutoSeed] Failed to get system employer');
          setAutoSeeding(false);
          return;
        }

        const result = await seed100Jobs(systemEmployerId);

        if (result.success) {
          console.log('[AutoSeed] Successfully seeded jobs:', result.message);
          localStorage.setItem('resuswipe_jobs_seeded', 'true');
          setSeedingComplete(true);
          showToast('success', `${result.inserted} jobs loaded successfully!`);
        }

        setAutoSeeding(false);
      } else {
        localStorage.setItem('resuswipe_jobs_seeded', 'true');
        setSeedingComplete(true);
      }
    } catch (error) {
      console.error('[AutoSeed] Error in auto-seeding:', error);
      setAutoSeeding(false);
    }
  };

  const loadUserResumes = async () => {
    if (!profile) return;
    const resumes = await resumeService.getUserResumes(profile.id);
    setUserResumes(resumes);

    const defaultId = await resumeTemplateService.getDefaultResume(profile.id);
    setDefaultResumeId(defaultId);

    if (defaultId) {
      setSelectedResumeId(defaultId);
    } else if (resumes.length > 0) {
      const primaryResume = resumes.find(r => r.is_primary);
      setSelectedResumeId(primaryResume?.id || resumes[0].id);
    }
  };

  const loadSwipeCounts = async () => {
    if (!profile?.id) return;

    try {
      const { data: skippedData } = await supabase
        .from('swipes')
        .select('job_id', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .eq('direction', 'left');

      const { data: savedData } = await supabase
        .from('swipes')
        .select('job_id', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .eq('direction', 'right');

      const { data: allSwipesData } = await supabase
        .from('swipes')
        .select('job_id')
        .eq('user_id', profile.id);

      const swipedJobIds = allSwipesData?.map(s => s.job_id) || [];

      const { count: newJobsCount } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')
        .not('id', 'in', swipedJobIds.length > 0 ? `(${swipedJobIds.join(',')})` : '()');

      setSwipeCounts({
        skipped: (skippedData as any)?.count || 0,
        saved: (savedData as any)?.count || 0,
        new: newJobsCount || 0,
      });
    } catch (error) {
      console.error('[SwipePage] Error loading swipe counts:', error);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    setCurrentIndex(0);
    await loadJobs();
    await loadSwipeCounts();
    setLoading(false);
    showToast('success', 'Jobs refreshed!');
  };

  const loadJobs = async () => {
    if (!profile?.id) {
      console.warn('[SwipePage] Cannot load jobs without profile');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      let query = supabase
        .from('jobs')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(50);

      if (viewMode === 'new') {
        // Show only jobs that haven't been swiped
        const { data: swipedJobs } = await supabase
          .from('swipes')
          .select('job_id')
          .eq('user_id', profile.id);

        const swipedJobIds = swipedJobs?.map(s => s.job_id) || [];

        if (swipedJobIds.length > 0) {
          query = query.not('id', 'in', `(${swipedJobIds.join(',')})`);
        }
      } else if (viewMode === 'skipped' || viewMode === 'saved') {
        // Show only jobs that were swiped in the specified direction
        const direction = viewMode === 'skipped' ? 'left' : 'right';
        const { data: swipedJobs } = await supabase
          .from('swipes')
          .select('job_id')
          .eq('user_id', profile.id)
          .eq('direction', direction);

        const swipedJobIds = swipedJobs?.map(s => s.job_id) || [];

        if (swipedJobIds.length === 0) {
          setJobs([]);
          setCurrentIndex(0);
          setLoading(false);
          return;
        }

        query = query.in('id', swipedJobIds);
      }

      if (filters.experienceLevel.length > 0) {
        query = query.in('experience_level', filters.experienceLevel);
      }

      if (filters.jobType.length > 0) {
        query = query.in('job_type', filters.jobType);
      }

      if (filters.remote !== null) {
        query = query.eq('remote', filters.remote);
      }

      if (filters.minSalary) {
        query = query.gte('salary_min', filters.minSalary);
      }

      if (filters.maxSalary) {
        query = query.lte('salary_max', filters.maxSalary);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[SwipePage] Error loading jobs:', error);
        throw error;
      }

      let filteredJobs = data || [];

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        filteredJobs = filteredJobs.filter(job =>
          job.title.toLowerCase().includes(q) ||
          job.company.toLowerCase().includes(q) ||
          job.description.toLowerCase().includes(q) ||
          job.skills.some((skill: string) => skill.toLowerCase().includes(q))
        );
      }

      if (filters.location.trim()) {
        const locationQuery = filters.location.toLowerCase();
        filteredJobs = filteredJobs.filter(job =>
          job.location.toLowerCase().includes(locationQuery)
        );
      }

      console.log('[SwipePage] Loaded jobs:', filteredJobs.length);
      setJobs(filteredJobs);
      setCurrentIndex(0);
    } catch (error) {
      console.error('[SwipePage] Error in loadJobs:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Core swipe handler (called by JobCard gesture OR our button animation flow).
   * - Logs the swipe to `swipes`
   * - On right swipe:
   *   - Awards +1 credit for interest (swipe_right)
   *   - Attempts RPC `swipe_to_apply` if resume available
   *   - Shows toast depending on outcome
   */
  const handleSwipe = async (direction: 'left' | 'right') => {
    const currentJob = jobs[currentIndex];
    if (!currentJob || !profile?.id) return;

    setSwipeHistory(prev => [...prev, { jobId: currentJob.id, direction }]);

    try {
      const { error: swipeError } = await supabase.from('swipes').insert({
        user_id: profile.id,
        job_id: currentJob.id,
        direction,
      });

      if (swipeError) {
        console.error('[Swipe] Error inserting swipe:', swipeError);
        if (swipeError.code !== '23505') {
          throw swipeError;
        }
      }

      if (direction === 'left') {
        // Left swipe → encouraging skip toast
        showToast('info', getNextToastMessage('left'));
      }

      if (direction === 'right') {
        // 1) Award +5 credits for showing interest (global rule)
        try {
          await awardCredits(
            profile.id,
            5,
            'swipe_right',
            `Showed interest in ${currentJob.title}`,
          );
        } catch (err) {
          console.error('[Swipe] Error awarding swipe_right credits:', err);
        }

        // 2) Attempt AI auto-apply via RPC if we have a resume
        const resumeIdToUse = selectedResumeId || defaultResumeId;
        const applicationType = determineApplicationType(currentJob);

        if (!resumeIdToUse) {
          // No resume yet → save-only experience
          showToast('info', getNextToastMessage('right'));
        } else {
          const { error: rpcError } = await supabase.rpc('swipe_to_apply', {
            job_id: currentJob.id,
            resume_id: resumeIdToUse,
            application_type: applicationType,
          });

          if (rpcError) {
            console.error('[Swipe] swipe_to_apply RPC error:', rpcError);

            if ((rpcError as any).message?.includes('INSUFFICIENT_CREDITS')) {
              // Saved but not enough credits for AI auto-apply
              showToast('warning', getNextToastMessage('lowCredits'));
            } else {
              // Saved but some other error with AI apply
              showToast('error', 'We saved this job, but AI auto-apply hit a snag.');
            }
          } else {
            // Auto-apply succeeded
            showToast('success', getNextToastMessage('right'));
            fetchCredits(profile.id).catch(err =>
              console.error('[Swipe] Error refreshing credits after auto-apply:', err),
            );
          }
        }
      }
    } catch (error) {
      console.error('[Swipe] Exception during swipe:', error);
      showToast('error', 'Something went wrong saving your swipe.');
    }

    setCurrentIndex(prev => prev + 1);
    await loadSwipeCounts();

    if (currentIndex >= jobs.length - 3) {
      loadJobs();
    }
  };

  /**
   * Button-driven swipe (X / Heart) → animate card THEN call handleSwipe.
   * This ensures any pass/like button shows a swipe animation on the current card.
   */
  const triggerButtonSwipe = (direction: 'left' | 'right') => {
    const currentJob = jobs[currentIndex];
    if (!currentJob) return;

    setCardSwipeAnimation(direction);

    // After animation completes, run the actual swipe logic.
    setTimeout(() => {
      setCardSwipeAnimation(null);
      handleSwipe(direction);
    }, 250);
  };

  const handleUndo = async () => {
    if (swipeHistory.length === 0 || currentIndex === 0 || !profile?.id) return;

    const lastSwipe = swipeHistory[swipeHistory.length - 1];
    setSwipeHistory(prev => prev.slice(0, -1));
    setCurrentIndex(prev => Math.max(prev - 1, 0));

    await supabase
      .from('swipes')
      .delete()
      .eq('user_id', profile.id)
      .eq('job_id', lastSwipe.jobId);

    await loadSwipeCounts();
  };

  const handleClearSwipes = async (clearType: 'skipped' | 'saved' | 'all') => {
    if (!profile?.id) return;

    try {
      let query = supabase
        .from('swipes')
        .delete()
        .eq('user_id', profile.id);

      if (clearType === 'skipped') {
        query = query.eq('direction', 'left');
      } else if (clearType === 'saved') {
        query = query.eq('direction', 'right');
      }

      const { error } = await query;

      if (error) throw error;

      showToast('success', `Successfully cleared ${clearType === 'all' ? 'all' : clearType} swipes`);
      setShowClearConfirm(false);
      setViewMode('new');
      await loadJobs();
      await loadSwipeCounts();
    } catch (error) {
      console.error('[SwipePage] Error clearing swipes:', error);
      showToast('error', 'Failed to clear swipe history');
    }
  };

  const handleAIApply = (job: Job) => {
    if (userResumes.length === 0) {
      navigate(`/jobseeker/resume-builder?returnUrl=/swipe`);
      return;
    }
    setSelectedJob(job);
    setShowApplyModal(true);
  };

  const handleFreeApply = (job: Job) => {
    setSelectedJob(job);
    setShowFreeApplyModal(true);
    setFreeApplyMode('upload');
    setResumeFile(null);
    setResumeText('');
    setCoverLetter('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
      ];
      if (allowedTypes.includes(file.type)) {
        setResumeFile(file);
      } else {
        alert('Please upload a PDF, DOC, DOCX, or TXT file');
      }
    }
  };

  // ✅ Free apply (manual) — unchanged logic, with upsert
  const handleSubmitFreeApplication = async () => {
    if (!profile || !selectedJob) return;

    if (freeApplyMode === 'upload' && !resumeFile) {
      alert('Please upload a resume file');
      return;
    }

    if (freeApplyMode === 'paste' && !resumeText.trim()) {
      alert('Please paste your resume text');
      return;
    }

    setSubmittingFreeApp(true);
    try {
      let resumeId: string | null = null;

      if (freeApplyMode === 'upload' && resumeFile) {
        const fileExt = resumeFile.name.split('.').pop();
        const fileName = `${profile.id}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(fileName, resumeFile);

        if (uploadError) throw uploadError;

        const { data: publicUrlData } = supabase.storage
          .from('resumes')
          .getPublicUrl(fileName);

        const { data: resumeData, error: resumeError } = await supabase
          .from('resumes')
          .insert({
            user_id: profile.id,
            title: `Resume for ${selectedJob.title}`,
            content: { fileName: resumeFile.name },
            file_url: publicUrlData.publicUrl,
            is_primary: false,
          })
          .select()
          .single();

        if (resumeError) throw resumeError;
        resumeId = resumeData.id;
      } else if (freeApplyMode === 'paste' && resumeText.trim()) {
        const { data: resumeData, error: resumeError } = await supabase
          .from('resumes')
          .insert({
            user_id: profile.id,
            title: `Resume for ${selectedJob.title}`,
            content: { text: resumeText },
            is_primary: false,
          })
          .select()
          .single();

        if (resumeError) throw resumeError;
        resumeId = resumeData.id;
      }

      if (!resumeId) {
        throw new Error('Failed to create resume');
      }

      const { error: appError } = await supabase
        .from('applications')
        .upsert(
          {
            job_id: selectedJob.id,
            applicant_id: profile.id,
            resume_id: resumeId,
            cover_letter: coverLetter.trim() || null,
            status: 'pending',
          },
          {
            onConflict: 'job_id,applicant_id',
          },
        );

      if (appError) throw appError;

      setApplicationSuccess(true);
      setTimeout(() => {
        setShowFreeApplyModal(false);
        setApplicationSuccess(false);
        setSelectedJob(null);
        setResumeFile(null);
        setResumeText('');
        setCoverLetter('');
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting free application:', error);
      alert(error.message || 'Failed to submit application');
    } finally {
      setSubmittingFreeApp(false);
    }
  };

  const handleSubmitAIApplication = async () => {
    if (!profile || !selectedJob || !selectedResumeId) return;

    const applicationType = determineApplicationType(selectedJob);
    const creditCost = getApplicationCost(applicationType);

    if (balance < creditCost) {
      alert('Insufficient credits. Please purchase more credits to continue.');
      return;
    }

    setApplyingJob(true);
    try {
      const result = await resumeService.submitAIJobApplication({
        userId: profile.id,
        jobId: selectedJob.id,
        applicationType,
        resumeId: selectedResumeId,
        generateCoverLetter: false,
      });

      if (result.success) {
        setApplicationSuccess(true);
        await fetchCredits(profile.id);
        setTimeout(() => {
          setShowApplyModal(false);
          setApplicationSuccess(false);
          setSelectedJob(null);
        }, 2000);
      } else {
        alert(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('An error occurred while submitting your application');
    } finally {
      setApplyingJob(false);
    }
  };

  const toggleFilter = (filterType: keyof typeof filters, value: any) => {
    if (filterType === 'experienceLevel' || filterType === 'jobType') {
      const currentArray = filters[filterType] as string[];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value];
      setFilters({ ...filters, [filterType]: newArray });
    } else if (filterType === 'remote') {
      setFilters({ ...filters, remote: filters.remote === value ? null : value });
    } else {
      setFilters({ ...filters, [filterType]: value });
    }
  };

  const clearAllFilters = () => {
    setFilters({
      experienceLevel: [],
      jobType: [],
      remote: null,
      minSalary: null,
      maxSalary: null,
      location: '',
    });
    setSearchQuery('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.experienceLevel.length > 0) count++;
    if (filters.jobType.length > 0) count++;
    if (filters.remote !== null) count++;
    if (filters.minSalary) count++;
    if (filters.maxSalary) count++;
    if (filters.location) count++;
    if (searchQuery) count++;
    return count;
  };

  const currentJob = jobs[currentIndex];

  if (loading || autoSeeding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          {autoSeeding && (
            <p className="text-gray-600 font-medium">Setting up your job discovery experience...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Discover Jobs</h1>
              <p className="text-gray-600 mt-1">Swipe right to show interest, left to pass</p>
            </div>
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow">
              <Coins className="w-5 h-5 text-amber-500" />
              <span className="font-semibold text-gray-900">{balance} credits</span>
            </div>
          </div>

          {/* View Mode Selector */}
          <div className="mb-6">
            <div className="bg-white rounded-xl shadow-lg p-4 border-2 border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-700">View Jobs:</h3>
                {(viewMode === 'skipped' || viewMode === 'saved') && (
                  <button
                    onClick={() => setShowClearConfirm(true)}
                    className="text-xs px-3 py-1 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Clear History
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setViewMode('new')}
                  className={`py-3 px-4 rounded-lg font-semibold transition flex flex-col items-center gap-1 ${
                    viewMode === 'new'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="text-sm">New Jobs</span>
                  <span className="text-xs opacity-80">({swipeCounts.new})</span>
                </button>
                <button
                  onClick={() => setViewMode('saved')}
                  className={`py-3 px-4 rounded-lg font-semibold transition flex flex-col items-center gap-1 ${
                    viewMode === 'saved'
                      ? 'bg-green-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Heart className="w-5 h-5" />
                  <span className="text-sm">Saved Jobs</span>
                  <span className="text-xs opacity-80">({swipeCounts.saved})</span>
                </button>
                <button
                  onClick={() => setViewMode('skipped')}
                  className={`py-3 px-4 rounded-lg font-semibold transition flex flex-col items-center gap-1 ${
                    viewMode === 'skipped'
                      ? 'bg-gray-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <EyeOff className="w-5 h-5" />
                  <span className="text-sm">Skipped Jobs</span>
                  <span className="text-xs opacity-80">({swipeCounts.skipped})</span>
                </button>
              </div>
              {viewMode !== 'new' && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-900">
                    {viewMode === 'saved'
                      ? 'Reviewing jobs you previously showed interest in. Swipe right again to apply!'
                      : 'Taking another look at jobs you previously skipped. Give them a second chance!'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="mb-8 space-y-4">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search jobs, companies, or skills..."
                  className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-6 py-3 rounded-xl font-semibold transition flex items-center gap-2 ${
                  showFilters || getActiveFiltersCount() > 0
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300'
                }`}
              >
                <SlidersHorizontal className="w-5 h-5" />
                Filters
                {getActiveFiltersCount() > 0 && (
                  <span className="bg-white text-orange-500 px-2 py-0.5 rounded-full text-xs font-bold">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>
            </div>

            {showFilters && (
              <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200 space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Filter Jobs</h3>
                  {getActiveFiltersCount() > 0 && (
                    <button
                      onClick={clearAllFilters}
                      className="text-sm text-orange-600 hover:text-orange-700 font-semibold"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Experience Level
                    </label>
                    <div className="space-y-2">
                      {['entry', 'mid', 'senior', 'executive'].map(level => (
                        <label key={level} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.experienceLevel.includes(level)}
                            onChange={() => toggleFilter('experienceLevel', level)}
                            className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-700 capitalize">{level}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Job Type
                    </label>
                    <div className="space-y-2">
                      {['full_time', 'part_time', 'contract', 'internship'].map(type => (
                        <label key={type} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.jobType.includes(type)}
                            onChange={() => toggleFilter('jobType', type)}
                            className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-700 capitalize">
                            {type.replace('_', ' ')}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Location
                    </label>
                    <input
                      type="text"
                      value={filters.location}
                      onChange={e => setFilters({ ...filters, location: e.target.value })}
                      placeholder="e.g., Detroit, Remote"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Work Location
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={filters.remote === true}
                          onChange={() => toggleFilter('remote', true)}
                          className="w-4 h-4 text-orange-500"
                        />
                        <span className="text-sm text-gray-700">Remote Only</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={filters.remote === false}
                          onChange={() => toggleFilter('remote', false)}
                          className="w-4 h-4 text-orange-500"
                        />
                        <span className="text-sm text-gray-700">On-Site Only</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={filters.remote === null}
                          onChange={() => setFilters({ ...filters, remote: null })}
                          className="w-4 h-4 text-orange-500"
                        />
                        <span className="text-sm text-gray-700">Both</span>
                      </label>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      <DollarSign className="w-4 h-4 inline mr-1" />
                      Salary Range
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <input
                          type="number"
                          value={filters.minSalary || ''}
                          onChange={e =>
                            setFilters({
                              ...filters,
                              minSalary: e.target.value ? parseInt(e.target.value) : null,
                            })
                          }
                          placeholder="Min ($)"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          value={filters.maxSalary || ''}
                          onChange={e =>
                            setFilters({
                              ...filters,
                              maxSalary: e.target.value ? parseInt(e.target.value) : null,
                            })
                          }
                          placeholder="Max ($)"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'} found
                  </p>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="px-6 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {userResumes.length === 0 && (
            <div className="bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 rounded-2xl p-8 mb-8 shadow-2xl border-2 border-white/20">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4 animate-pulse">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Get Ready to Apply!</h3>
                <p className="text-white/95 text-lg mb-4 max-w-md">
                  You found the perfect job. Now build your professional resume to apply with AI in
                  just minutes.
                </p>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 w-full max-w-md">
                  <div className="flex items-center justify-between text-white mb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-yellow-300" />
                      <span className="font-semibold">AI-Powered Builder</span>
                    </div>
                    <Check className="w-5 h-5 text-green-300" />
                  </div>
                  <div className="flex items-center justify-between text-white mb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-300" />
                      <span className="font-semibold">ATS-Optimized</span>
                    </div>
                    <Check className="w-5 h-5 text-green-300" />
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-yellow-300" />
                      <span className="font-semibold">5 Pro Templates</span>
                    </div>
                    <Check className="w-5 h-5 text-green-300" />
                  </div>
                </div>

                <button
                  onClick={() => navigate(`/jobseeker/resume-builder?returnUrl=/swipe`)}
                  className="w-full max-w-md py-4 bg-white text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600 rounded-xl font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-105 flex items-center justify-center gap-3 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white to-gray-50 group-hover:from-gray-50 group-hover:to-white transition-all"></div>
                  <span className="relative text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600 flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-orange-600" />
                    Build My Resume Now
                    <ArrowRight className="w-6 h-6 text-amber-600" />
                  </span>
                </button>

                <div className="mt-4 flex items-center gap-2 text-white/90 text-sm">
                  <Coins className="w-4 h-4 text-yellow-300" />
                  <span>Only 10 credits • Takes 5 minutes</span>
                </div>

                <p className="text-white/70 text-xs mt-2">Current balance: {balance} credits</p>
              </div>
            </div>
          )}

          {!currentJob ? (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Briefcase className="w-8 h-8 text-gray-400" />
              </div>
              {viewMode === 'new' ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">No more new jobs</h2>
                  <p className="text-gray-600 mb-6">You've seen all available jobs. Check back later for new opportunities!</p>
                  {(swipeCounts.saved > 0 || swipeCounts.skipped > 0) && (
                    <div className="mb-6 space-y-3">
                      <p className="text-sm text-gray-600">Want to review jobs you've already seen?</p>
                      <div className="flex justify-center gap-3">
                        {swipeCounts.saved > 0 && (
                          <button
                            onClick={() => setViewMode('saved')}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition flex items-center gap-2"
                          >
                            <Heart className="w-4 h-4" />
                            View {swipeCounts.saved} Saved Jobs
                          </button>
                        )}
                        {swipeCounts.skipped > 0 && (
                          <button
                            onClick={() => setViewMode('skipped')}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition flex items-center gap-2"
                          >
                            <EyeOff className="w-4 h-4" />
                            Review {swipeCounts.skipped} Skipped
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={handleRefresh}
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </>
              ) : viewMode === 'saved' ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">No saved jobs</h2>
                  <p className="text-gray-600 mb-6">Start swiping right on jobs you're interested in!</p>
                  <button
                    onClick={() => setViewMode('new')}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition inline-flex items-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    View New Jobs
                  </button>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">No skipped jobs</h2>
                  <p className="text-gray-600 mb-6">You haven't skipped any jobs yet, or you've given them all a second look!</p>
                  <button
                    onClick={() => setViewMode('new')}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition inline-flex items-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    View New Jobs
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="relative w-full max-w-sm h-[600px] mb-8">
                {jobs.slice(currentIndex, currentIndex + 3).map((job, index) => {
                  const isTop = index === 0;
                  const baseScale = 1 - index * 0.05;
                  let transform = `translateY(${index * 10}px) scale(${baseScale})`;
                  let transition: string | undefined;

                  if (isTop && cardSwipeAnimation === 'left') {
                    transform = 'translate(-160%, -40px) rotate(-12deg) scale(1)';
                    transition = 'transform 0.25s ease-out';
                  } else if (isTop && cardSwipeAnimation === 'right') {
                    transform = 'translate(160%, -40px) rotate(12deg) scale(1)';
                    transition = 'transform 0.25s ease-out';
                  }

                  return (
                    <JobCard
                      key={job.id}
                      job={job}
                      onSwipe={isTop ? handleSwipe : () => {}}
                      style={{
                        zIndex: 3 - index,
                        transform,
                        transition,
                      }}
                    />
                  );
                })}
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={() => triggerButtonSwipe('left')}
                  className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-red-50 transition group"
                  aria-label="Pass"
                >
                  <X className="w-8 h-8 text-red-500 group-hover:scale-110 transition" />
                </button>

                {swipeHistory.length > 0 && (
                  <button
                    onClick={handleUndo}
                    className="w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition group"
                    aria-label="Undo"
                  >
                    <RotateCcw className="w-6 h-6 text-gray-600 group-hover:scale-110 transition" />
                  </button>
                )}

                <button
                  onClick={() => triggerButtonSwipe('right')}
                  className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-green-50 transition group"
                  aria-label="Like"
                >
                  <Heart className="w-8 h-8 text-green-500 group-hover:scale-110 transition" />
                </button>
              </div>

              <div className="mt-6 text-center text-sm text-gray-500">
                {jobs.length - currentIndex} jobs remaining
              </div>

              {currentJob && (
                <div className="mt-8 max-w-sm w-full">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-6 text-white shadow-xl">
                    <div className="flex items-start gap-3 mb-4">
                      <Sparkles className="w-6 h-6 flex-shrink-0" />
                      <div>
                        <h3 className="font-bold text-lg mb-1">Apply with AI</h3>
                        <p className="text-sm text-white/90">
                          Let AI customize your resume and apply instantly
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAIApply(currentJob)}
                      className="w-full py-3 bg-white text-orange-600 rounded-xl font-bold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <Zap className="w-5 h-5" />
                      Apply for {getApplicationCost(determineApplicationType(currentJob))} Credits
                    </button>
                    <div className="mt-3 text-center text-xs text-white/80">
                      Or{' '}
                      <button
                        onClick={() => handleFreeApply(currentJob)}
                        className="underline font-semibold hover:text-white transition"
                      >
                        apply manually (free)
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showApplyModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">AI Job Application</h2>

              {applicationSuccess ? (
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-green-900 mb-1">Application Submitted!</h3>
                      <p className="text-green-800">
                        Your AI-customized application has been sent successfully.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <h3 className="font-bold text-gray-900 mb-2">{selectedJob.title}</h3>
                    <p className="text-sm text-gray-600">{selectedJob.company}</p>
                    <p className="text-sm text-gray-600">{selectedJob.location}</p>
                    {selectedJob.salary_min && selectedJob.salary_max && (
                      <p className="text-sm font-semibold text-gray-900 mt-2">
                        ${(selectedJob.salary_min / 1000).toFixed(0)}k - $
                        {(selectedJob.salary_max / 1000).toFixed(0)}k
                      </p>
                    )}
                  </div>

                  <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-cyan-900">
                        <p className="font-semibold mb-1">AI will:</p>
                        <ul className="space-y-1">
                          <li>• Customize your resume for this specific job</li>
                          <li>• Highlight your most relevant experience</li>
                          <li>• Submit your application instantly</li>
                          <li>• Track your application status</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {userResumes.length === 0 ? (
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-6 mb-6">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FileText className="w-8 h-8 text-orange-600" />
                        </div>
                        <h3 className="text-lg font-bold text-orange-900 mb-2">
                          Resume Required
                        </h3>
                        <p className="text-sm text-orange-800 mb-4">
                          Build your professional resume now to unlock AI-powered job applications.
                          Takes just 5 minutes!
                        </p>
                        <div className="bg-white rounded-lg p-3 mb-4 text-left">
                          <div className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                            <Check className="w-4 h-4 text-green-600" />
                            <span>5 professional templates</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                            <Check className="w-4 h-4 text-green-600" />
                            <span>AI-optimized content</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 text-green-600" />
                            <span>Apply to unlimited jobs</span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setShowApplyModal(false);
                            navigate(
                              `/jobseeker/resume-builder?returnUrl=/swipe&jobId=${selectedJob.id}`,
                            );
                          }}
                          className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition-all flex items-center justify-center gap-2"
                        >
                          <Sparkles className="w-5 h-5" />
                          Build Resume Now (10 credits)
                          <ArrowRight className="w-5 h-5" />
                        </button>
                        <p className="text-xs text-gray-600 mt-3">
                          You have {balance} credits available
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Select Resume
                        {defaultResumeId && selectedResumeId === defaultResumeId && (
                          <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold">
                            ✓ Default
                          </span>
                        )}
                      </label>
                      <select
                        value={selectedResumeId || ''}
                        onChange={e => setSelectedResumeId(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                      >
                        {userResumes.map(resume => (
                          <option key={resume.id} value={resume.id}>
                            {resume.title}
                            {defaultResumeId === resume.id ? ' (Default)' : ''}
                            {resume.is_primary ? ' (Primary)' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-2">
                        {defaultResumeId && selectedResumeId === defaultResumeId
                          ? 'Using your default resume for quick applications'
                          : 'Change your default resume in My Resumes'}
                      </p>
                    </div>
                  )}

                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl p-4 text-white mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-90 mb-1">Credit Cost</p>
                        <p className="text-2xl font-bold">
                          {getApplicationCost(determineApplicationType(selectedJob))} credits
                        </p>
                      </div>
                      <Coins className="w-10 h-10 opacity-80" />
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/20 text-sm">
                      Balance after:{' '}
                      {balance - getApplicationCost(determineApplicationType(selectedJob))} credits
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowApplyModal(false);
                        setSelectedJob(null);
                      }}
                      className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors"
                      disabled={applyingJob}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitAIApplication}
                      disabled={applyingJob || userResumes.length === 0 || !selectedResumeId}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {applyingJob ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Applying...
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5" />
                          Apply Now
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showFreeApplyModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Apply Manually (Free)</h2>

              {applicationSuccess ? (
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-green-900 mb-1">Application Submitted!</h3>
                      <p className="text-green-800">Your application has been sent successfully.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <h3 className="font-bold text-gray-900 mb-2">{selectedJob.title}</h3>
                    <p className="text-sm text-gray-600">{selectedJob.company}</p>
                    <p className="text-sm text-gray-600">{selectedJob.location}</p>
                    {selectedJob.salary_min && selectedJob.salary_max && (
                      <p className="text-sm font-semibold text-gray-900 mt-2">
                        ${(selectedJob.salary_min / 1000).toFixed(0)}k - $
                        {(selectedJob.salary_max / 1000).toFixed(0)}k
                      </p>
                    )}
                  </div>

                  <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-green-900">
                        <p className="font-semibold mb-1">Free Manual Application</p>
                        <p>No credits required. Upload or paste your resume and apply directly.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="flex gap-4 mb-4">
                      <button
                        onClick={() => setFreeApplyMode('upload')}
                        className={`flex-1 py-3 px-4 rounded-xl font-semibold transition flex items-center justify-center gap-2 ${
                          freeApplyMode === 'upload'
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        <Upload className="w-5 h-5" />
                        Upload Resume
                      </button>
                      <button
                        onClick={() => setFreeApplyMode('paste')}
                        className={`flex-1 py-3 px-4 rounded-xl font-semibold transition flex items-center justify-center gap-2 ${
                          freeApplyMode === 'paste'
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        <FileText className="w-5 h-5" />
                        Paste Resume
                      </button>
                    </div>

                    {freeApplyMode === 'upload' ? (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Upload Resume File
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-orange-500 transition">
                          <FileUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <input
                            type="file"
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx,.txt"
                            className="hidden"
                            id="resume-upload"
                          />
                          <label
                            htmlFor="resume-upload"
                            className="cursor-pointer inline-block px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
                          >
                            Choose File
                          </label>
                          <p className="text-sm text-gray-600 mt-2">
                            PDF, DOC, DOCX, or TXT (max 10MB)
                          </p>
                          {resumeFile && (
                            <p className="text-sm text-green-600 font-semibold mt-2">
                              ✓ {resumeFile.name}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Paste Resume Text
                        </label>
                        <textarea
                          value={resumeText}
                          onChange={e => setResumeText(e.target.value)}
                          rows={10}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                          placeholder="Paste your complete resume text here including work experience, education, skills, etc."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {resumeText.length} characters
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cover Letter (Optional)
                    </label>
                    <textarea
                      value={coverLetter}
                      onChange={e => setCoverLetter(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                      placeholder="Write a brief cover letter explaining why you're interested in this position..."
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowFreeApplyModal(false);
                        setSelectedJob(null);
                        setResumeFile(null);
                        setResumeText('');
                        setCoverLetter('');
                      }}
                      className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors"
                      disabled={submittingFreeApp}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitFreeApplication}
                      disabled={
                        submittingFreeApp ||
                        (freeApplyMode === 'upload' && !resumeFile) ||
                        (freeApplyMode === 'paste' && !resumeText.trim())
                      }
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl font-bold hover:from-green-600 hover:to-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submittingFreeApp ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Check className="w-5 h-5" />
                          Submit Application
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Clear Swipes Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Clear Swipe History?</h3>
                <p className="text-sm text-gray-600">
                  Choose which jobs to remove from your swipe history. This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <button
                onClick={() => handleClearSwipes('skipped')}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition text-left flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <EyeOff className="w-5 h-5" />
                  Clear Skipped Jobs Only
                </span>
                <span className="text-sm text-gray-600">({swipeCounts.skipped})</span>
              </button>

              <button
                onClick={() => handleClearSwipes('saved')}
                className="w-full py-3 px-4 bg-green-50 hover:bg-green-100 text-green-900 rounded-lg font-semibold transition text-left flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  Clear Saved Jobs Only
                </span>
                <span className="text-sm text-green-700">({swipeCounts.saved})</span>
              </button>

              <button
                onClick={() => handleClearSwipes('all')}
                className="w-full py-3 px-4 bg-red-50 hover:bg-red-100 text-red-900 rounded-lg font-semibold transition text-left flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <Trash2 className="w-5 h-5" />
                  Clear All Swipe History
                </span>
                <span className="text-sm text-red-700">
                  ({swipeCounts.skipped + swipeCounts.saved})
                </span>
              </button>
            </div>

            <button
              onClick={() => setShowClearConfirm(false)}
              className="w-full py-3 px-4 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* 🔔 Global Toast */}
      {toast && (
        <div
          className={`
            fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-3 rounded-xl shadow-lg
            max-w-md w-[90%] text-sm font-medium text-white
            ${toast.type === 'success' ? 'bg-emerald-500'
              : toast.type === 'error' ? 'bg-red-500'
              : toast.type === 'warning' ? 'bg-amber-500'
              : 'bg-gray-900'}
          `}
        >
          {toast.message}
        </div>
      )}
    </>
  );
}
